"use strict";
// Copyright © Veeam Software Group GmbH
Object.defineProperty(exports, "__esModule", { value: true });
exports.WeeklyScheduleHarmonizationManager = void 0;
require("@veeam/components/lib/SchedulingMatrix/types");
require("../../core");
const helpers_1 = require("../../helpers");
const constants_1 = require("./constants");
const DailyScheduleHarmonizationManager_1 = require("./DailyScheduleHarmonizationManager");
const helpers_2 = require("./helpers");
const intersect_1 = require("./helpers/intersect");
require("./models");
const ScheduleHarmonizationManager_1 = require("./ScheduleHarmonizationManager");
const types_1 = require("./types");
class WeeklyScheduleHarmonizationManager extends ScheduleHarmonizationManager_1.ScheduleHarmonizationManager {
    constructor(mainModel, controlModel, models, dailyRetentionFieldsSettings, weeklyRetentionFieldsSettings) {
        super(Object.assign({ weeklyScheduleModel: mainModel }, models));
        this.mainModel = mainModel;
        this.controlModel = controlModel;
        this.models = models;
        this.dailyRetentionFieldsSettings = dailyRetentionFieldsSettings;
        this.weeklyRetentionFieldsSettings = weeklyRetentionFieldsSettings;
        this.isMatrixColumnSelectable = ({ columnIndex }) => {
            const dailyModelValue = this.models.dailyScheduleModel.getValue();
            const isDailyScheduleEnabled = dailyModelValue.isEnabled;
            if (!isDailyScheduleEnabled) {
                return false;
            }
            const isEveryday = this.models.dailyScheduleModel.isDailyKindEveryday();
            const isWeekdays = this.models.dailyScheduleModel.isDailyKindWeekdays();
            const isSelectedDays = this.models.dailyScheduleModel.isDailyKindSelectedDays();
            if (isEveryday) {
                return false;
            }
            let daysIndex = [];
            if (isWeekdays) {
                daysIndex = helpers_2.getDaysOfWeekIndexList();
            }
            if (isSelectedDays) {
                dailyModelValue.fields.days.forEach((dayOfWeek) => {
                    daysIndex.push(types_1.DAY_INDEX[dayOfWeek]);
                });
            }
            return !daysIndex.includes(columnIndex);
        };
        this.getFieldTimeOptions = () => {
            const availableOptions = this.getAvailableOptions();
            return availableOptions.time.firstRow;
        };
        this.isFieldTimeDisabled = () => {
            const fieldTimeOptions = this.getFieldTimeOptions();
            return fieldTimeOptions.length === 1;
        };
        this.getAvailableOptions = () => {
            const dailyAvailableOptions = this.dailyScheduleHarmonizationManager.getAvailableOptions();
            return dailyAvailableOptions;
        };
        this.getCurrentState = () => {
            return WeeklyScheduleHarmonizationManager.getCurrentStateData(this.mainModel.getValue());
        };
        this.harmonize = (shouldResetSchedule = true) => {
            const dailyScheduleModelValue = this.models.dailyScheduleModel.getValue();
            const availableOptions = this.getAvailableOptions();
            const modelValue = this.mainModel.getValue();
            const isReset = shouldResetSchedule && !modelValue.isEnabled && modelValue.isModified;
            // reset
            if (isReset) {
                return WeeklyScheduleHarmonizationManager.getMainModelInitialValue(modelValue.rows.first.isEnabled, modelValue.rows.second.isEnabled, modelValue.rows.third.isEnabled, modelValue.rows.first.fields.isTypeVisible, modelValue.rows.second.fields.isTypeVisible, modelValue.rows.third.fields.isTypeVisible, this.weeklyRetentionFieldsSettings, dailyScheduleModelValue);
            }
            // Maksim.Kovalevskii: we use restore point / time options only from 'first row' because it doesn't matter for Weekly Schedule
            // since it doesn't have other options (for example - third row (Archive))
            let restorePoint = WeeklyScheduleHarmonizationManager.getCurrentOrFirstInListItem(modelValue.fields.time, this.getFieldTimeOptions());
            let firstRowSchedule = WeeklyScheduleHarmonizationManager.getHarmonizedScheduleValue(modelValue.rows.first.isEnabled, modelValue.isModified, modelValue.rows.first.schedule, availableOptions.dayOfWeek.firstRow);
            let secondRowSchedule = WeeklyScheduleHarmonizationManager.getHarmonizedScheduleValue(modelValue.rows.second.isEnabled, modelValue.isModified, modelValue.rows.second.schedule, availableOptions.dayOfWeek.secondRow);
            let thirdRowSchedule = WeeklyScheduleHarmonizationManager.getHarmonizedScheduleValue(modelValue.rows.third.isEnabled, modelValue.isModified, modelValue.rows.third.schedule, availableOptions.dayOfWeek.thirdRow);
            return Object.assign(Object.assign({}, modelValue), { rows: Object.assign(Object.assign({}, modelValue.rows), { first: Object.assign(Object.assign({}, modelValue.rows.first), { schedule: firstRowSchedule }), second: Object.assign(Object.assign({}, modelValue.rows.second), { schedule: secondRowSchedule }), third: Object.assign(Object.assign({}, modelValue.rows.third), { schedule: thirdRowSchedule }) }), fields: Object.assign(Object.assign({}, modelValue.fields), { time: restorePoint }) });
        };
        this.mainModel = mainModel;
        this.controlModel = controlModel;
        this.models = models;
        this.dailyScheduleHarmonizationManager = new DailyScheduleHarmonizationManager_1.DailyScheduleHarmonizationManager(this.models.dailyScheduleModel, null, Object.assign(Object.assign({}, this.models), { weeklyScheduleModel: this.mainModel }), dailyRetentionFieldsSettings);
        this.dailyRetentionFieldsSettings = dailyRetentionFieldsSettings;
        this.weeklyRetentionFieldsSettings = weeklyRetentionFieldsSettings;
    }
}
exports.WeeklyScheduleHarmonizationManager = WeeklyScheduleHarmonizationManager;
WeeklyScheduleHarmonizationManager.ROW_FIRST_FIELD_COUNT_MIN_VALUE = constants_1.RETENTION_COUNT_MIN;
WeeklyScheduleHarmonizationManager.getMainModelInitialValue = (isFirstRowScheduleVisible = true, isSecondRowScheduleVisible = true, isThirdRowScheduleVisible = true, isRowFirstFieldTypeVisible = false, isRowSecondFieldTypeVisible = false, isRowThirdFieldTypeVisible = true, retentionFieldsSettings, dailyScheduleModelValue) => {
    const timeOptions = WeeklyScheduleHarmonizationManager.getFieldTimeOptionsData(dailyScheduleModelValue);
    return {
        isEnabled: false,
        isModified: false,
        rows: {
            [types_1.SCHEDULE_ROW.first]: WeeklyScheduleHarmonizationManager.getRowFirstInitialValue(isFirstRowScheduleVisible, constants_1.DAYS_PER_WEEK, retentionFieldsSettings.rowFirstFieldCountInitialValue, retentionFieldsSettings.rowFirstFieldTypeInitialValue, isRowFirstFieldTypeVisible),
            [types_1.SCHEDULE_ROW.second]: WeeklyScheduleHarmonizationManager.getRowSecondInitialValue(isSecondRowScheduleVisible, constants_1.DAYS_PER_WEEK, retentionFieldsSettings.rowSecondFieldCountInitialValue, retentionFieldsSettings.rowSecondFieldTypeInitialValue, isRowSecondFieldTypeVisible),
            [types_1.SCHEDULE_ROW.third]: WeeklyScheduleHarmonizationManager.getRowThirdInitialValue(isThirdRowScheduleVisible, constants_1.DAYS_PER_WEEK, retentionFieldsSettings.rowThirdFieldCountInitialValue, retentionFieldsSettings.rowThirdFieldTypeInitialValue, isRowThirdFieldTypeVisible),
        },
        fields: {
            time: timeOptions[0] || WeeklyScheduleHarmonizationManager.FIELD_TIME_INITIAL_VALUE,
        },
    };
};
WeeklyScheduleHarmonizationManager.getFieldTimeOptionsData = (dailyScheduleModelValue) => {
    const availableOptions = WeeklyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue);
    return availableOptions.time.firstRow;
};
WeeklyScheduleHarmonizationManager.getMonthCurrentOptionsData = () => {
    return helpers_2.getMonthsOfYearList();
};
WeeklyScheduleHarmonizationManager.getTimeCurrentOptionsData = (modelValue) => {
    const defaultTimes = helpers_1.getTimes();
    return modelValue.isEnabled ? [modelValue.fields.time] : defaultTimes;
};
WeeklyScheduleHarmonizationManager.getDayOfWeekCurrentOptionsData = (modelValue, row) => {
    const defaultDays = helpers_2.getEverydayList();
    const daysSchedule = helpers_2.getDayOfWeekDataFromSchedule(modelValue.rows[row].schedule);
    const isEnabled = row === 'third' ? modelValue.isEnabled && daysSchedule.length > 0 : modelValue.isEnabled;
    return isEnabled ? daysSchedule : defaultDays;
};
WeeklyScheduleHarmonizationManager.getAvailableOptionsData = (dailyScheduleModelValue) => {
    const dailyAvailableOptions = DailyScheduleHarmonizationManager_1.DailyScheduleHarmonizationManager.getAvailableOptionsData(dailyScheduleModelValue);
    return dailyAvailableOptions;
};
WeeklyScheduleHarmonizationManager.getCurrentStateData = (weeklyScheduleModelValue) => {
    const monthOptions = WeeklyScheduleHarmonizationManager.getMonthCurrentOptionsData();
    const timeOptions = WeeklyScheduleHarmonizationManager.getTimeCurrentOptionsData(weeklyScheduleModelValue);
    const defaultDayNumberInMonth = helpers_2.getDayNumberInMonthList();
    const dayOfWeekFirstRow = WeeklyScheduleHarmonizationManager.getDayOfWeekCurrentOptionsData(weeklyScheduleModelValue, 'first');
    const dayOfWeekSecondRow = WeeklyScheduleHarmonizationManager.getDayOfWeekCurrentOptionsData(weeklyScheduleModelValue, 'second');
    const dayOfWeekThirdRow = WeeklyScheduleHarmonizationManager.getDayOfWeekCurrentOptionsData(weeklyScheduleModelValue, 'third');
    return {
        time: {
            firstRow: timeOptions,
            secondRow: timeOptions,
            thirdRow: timeOptions,
        },
        dayOfWeek: {
            firstRow: dayOfWeekFirstRow,
            secondRow: dayOfWeekSecondRow,
            thirdRow: dayOfWeekThirdRow,
        },
        monthOfYear: {
            firstRow: monthOptions,
            secondRow: monthOptions,
            thirdRow: monthOptions,
        },
        dayNumberInMonth: {
            firstRow: defaultDayNumberInMonth,
            secondRow: defaultDayNumberInMonth,
            thirdRow: defaultDayNumberInMonth,
        },
    };
};
WeeklyScheduleHarmonizationManager.getHarmonizedScheduleValue = (isEnabled, isModified, currentSchedule, availableOptionsDayOfWeek) => {
    // Maksim.Kovalevskii: if some schedule is not enabled (backups or replicas) or
    // if we open panel to set Weekly Schedule for the first time or
    // if there are not selected schedule's items
    if (!isEnabled || !isModified || !currentSchedule.some((val) => val === true)) {
        return currentSchedule;
    }
    let schedule = helpers_2.getDayOfWeekDataFromSchedule(currentSchedule);
    const intersectedFirstRowSchedule = intersect_1.intersect(availableOptionsDayOfWeek, schedule);
    // if (modelValue.isModified && intersectedFirstRowSchedule.length > 0) {
    if (intersectedFirstRowSchedule.length > 0) {
        schedule = intersectedFirstRowSchedule;
    }
    else {
        schedule = [availableOptionsDayOfWeek[0]];
    }
    return helpers_2.getWeeksMatrixData(schedule);
};
//# sourceMappingURL=WeeklyScheduleHarmonizationManager.js.map